<template lang="pug">
    .fiskaly-configuration(v-if="isTseEnabled")
        h4
            i.el-icon-fa-receipt.fas
            | {{ $t('Fiscal Compliance (TSE)') }}

        template(v-if="fiskalyConfiguration")
            el-descriptions(:column="1" border size="small")
                el-descriptions-item(:label="$t('Organization ID')")
                    span {{ fiskalyConfiguration.fiskaly_organization_id || '—' }}
                el-descriptions-item(:label="$t('TSS ID')")
                    span {{ fiskalyConfiguration.tss_id || '—' }}
                el-descriptions-item(:label="$t('TSS Status')")
                    el-tag(
                        :type="tssStateTagType"
                        size="small"
                    ) {{ $t('fiskaly.' + fiskalyConfiguration.tss_state) || '—' }}
            el-button(
                v-if="fiskalyConfiguration.tss_state !== 'INITIALIZED'"
                type="warning"
                size="mini"
                plain
                round
                icon="el-icon-fa-rotate fas"
                :loading="provisioning"
                @click="provisionFiskaly"
            ) {{ $t('Re-provision') }}

        template(v-else)
            el-alert(
                :title="$t('No Fiskaly resources provisioned yet.')"
                type="info"
                show-icon
                :closable="false"
            )
            el-alert(
                v-if="missingFiskalyRequirements.length"
                :title="$t('Missing data required for Fiskaly provisioning')"
                type="warning"
                show-icon
                :closable="false"
            )
                ul.missing-fiskaly-requirements
                    li(v-for="item in missingFiskalyRequirements" :key="item") {{ $t(item) }}
            el-button(
                type="primary"
                size="mini"
                plain
                round
                icon="el-icon-fa-plug fas"
                :loading="provisioning"
                :disabled="missingFiskalyRequirements.length > 0"
                @click="provisionFiskaly"
            ) {{ $t('Provision Fiskaly Resources') }}
</template>

<i18n>
{
    "en": {
        "fiskaly.CREATED": "Created",
        "fiskaly.UNINITIALIZED": "Uninitialized",
        "fiskaly.INITIALIZED": "Initialized",
        "fiskaly.DISABLED": "Disabled",
        "fiskaly.DELETED": "Deleted",
        "fiskaly.DEFECTIVE": "Defective",
        "fiskaly.EVICTED": "Evicted"
    },
    "de": {
        "Fiscal Compliance (TSE)": "Fiskalisierung (TSE)",
        "Organization ID": "Organisations-ID",
        "TSS ID": "TSS-ID",
        "TSS Status": "TSS-Status",
        "Re-provision": "Neu bereitstellen",
        "No Fiskaly resources provisioned yet.": "Noch keine Fiskaly-Ressourcen bereitgestellt.",
        "Provision Fiskaly Resources": "Fiskaly-Ressourcen bereitstellen",
        "Missing data required for Fiskaly provisioning": "Für die Fiskaly-Bereitstellung fehlen folgende Daten",
        "Shop VAT ID (USt-IdNr) is missing": "USt-IdNr des Shops fehlt",
        "Shop tax number (Steuernummer) is missing": "Steuernummer des Shops fehlt",
        "Shop address or CMS owner address is incomplete": "Shop-Adresse oder Inhaberadresse ist unvollständig",
        "fiskaly.CREATED": "Erstellt",
        "fiskaly.UNINITIALIZED": "Nicht initialisiert",
        "fiskaly.INITIALIZED": "Initialisiert",
        "fiskaly.DISABLED": "Deaktiviert",
        "fiskaly.DELETED": "Gelöscht",
        "fiskaly.DEFECTIVE": "Defekt",
        "fiskaly.EVICTED": "Entfernt"
    }
}
</i18n>

<script>
import _ from 'lodash';
import { adminClient as apolloClient } from '@/store/apollo';
import ProvisionFiskalyGql from '@/store/graphql/cms/mutations/ProvisionFiskaly.gql';

export default {
    name: 'FiskalyConfiguration',
    props: {
        cms: {
            type: Object,
            required: true,
        },
    },
    data() {
        return {
            provisioning: false,
        };
    },
    computed: {
        isTseEnabled() {
            return _.get(this.cms, 'is_tse', false);
        },
        fiskalyConfiguration() {
            return _.get(this.cms, 'fiskalyConfiguration', null);
        },
        tssStateTagType() {
            const state = _.get(this.fiskalyConfiguration, 'tss_state');
            if (state === 'INITIALIZED') return 'success';
            if (state === 'DISABLED') return 'danger';
            return 'warning';
        },
        missingFiskalyRequirements() {
            const missing = [];

            if (!_.get(this.cms, 'shop.tax_id')) {
                missing.push('Shop VAT ID (USt-IdNr) is missing');
            }
            if (!_.get(this.cms, 'shop.tax_nr')) {
                missing.push('Shop tax number (Steuernummer) is missing');
            }

            // cms.address is an accessor that returns the owner's user address
            // (see Cms::getAddressAttribute) — that's the fallback when the shop
            // has no address of its own.
            const addressFields = ['street', 'zipcode', 'city', 'country'];
            const shopHasFullAddress = addressFields.every((field) => !!_.get(this.cms, 'shop.address.' + field));
            const ownerHasFullAddress = addressFields.every((field) => !!_.get(this.cms, 'address.' + field));

            if (!shopHasFullAddress && !ownerHasFullAddress) {
                missing.push('Shop address or CMS owner address is incomplete');
            }

            return missing;
        },
    },
    methods: {
        provisionFiskaly() {
            this.provisioning = true;
            apolloClient.mutate({
                mutation: ProvisionFiskalyGql,
                variables: { cmsId: parseInt(this.cms.id) },
            })
                .then(() => {
                    this.$notify.success({
                        title: this.$t('Provisioning started. Resources will be ready shortly.'),
                        position: 'bottom-left',
                        customClass: 'dark',
                    });
                })
                .catch((error) => {
                    const backendMessage = _.get(error, 'graphQLErrors[0].message')
                        || _.get(error, 'networkError.result.errors[0].message')
                        || _.get(error, 'message');

                    this.$notify.error({
                        title: backendMessage || this.$t('Failed to start Fiskaly provisioning.'),
                        position: 'bottom-left',
                        customClass: 'dark',
                    });
                })
                .finally(() => {
                    this.provisioning = false;
                });
        },
    },
};
</script>

<style lang="sass" scoped>
.fiskaly-configuration
    margin-top: 1rem

    .el-alert
        margin-bottom: 0.75rem

    .el-button
        margin-top: 0.75rem

    .missing-fiskaly-requirements
        margin: 0.25rem 0 0
        padding-left: 1.25rem
</style>
